import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Form as BootstrapForm } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';

const validate = Yup.object({
    itemName: Yup.string().required('Required'),
    type: Yup.string().oneOf(['price', 'discount']).required('Required'),
    price: Yup.number()
        .transform((value) => (value === '' ? undefined : value))
        .when('type', {
            is: 'price',
            then: (schema) => schema.required('Required').min(0, 'Must be at least 0'),
            otherwise: (schema) => schema.notRequired()
        }),
    discountPercent: Yup.number()
        .transform((value) => (value === '' ? undefined : value))
        .when('type', {
            is: 'discount',
            then: (schema) => schema.required('Required').min(0, 'Must be at least 0').max(100, 'Cannot exceed 100'),
            otherwise: (schema) => schema.notRequired()
        })
});

function AddEditMenuItem() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { getToken } = useAuth();
    const isEdit = Boolean(state?.id || id);

    const [menuItemDetails, setMenuItemDetails] = useState(null);

    useEffect(() => {
        if (isEdit && (state?.id || id)) {
            // Fetch menu item by id from API
            const fetchMenuItem = async () => {
                const token = getToken();
                const menuItemId = state?.id || id;
                if (token && menuItemId) {
                    const res = await new WebApiCalls().getMenuItem(token, menuItemId);
                    if (res?.code === 200) {
                        setMenuItemDetails(res.data);
                    }
                }
            };
            fetchMenuItem();
        } else if (state && state.prefill) {
            setMenuItemDetails(state.prefill);
        }
    }, [id, isEdit, state, getToken]);

    const handleSubmit = async (values) => {
        const token = getToken();
        if (!token) return;
        let res;
        if (isEdit && (state?.id || id)) {
            // Update
            res = await new WebApiCalls().updateMenuItem(token, state?.id || id, values);
        } else {
            // Create
            res = await new WebApiCalls().createMenuItem(token, values);
        }
        if (res?.code === 200) {
            toast.success(isEdit ? 'Menu item updated successfully.' : 'Menu item added successfully.');
            navigate(state?.redirect_url || '/menu-items');
        } else {
            toast.error('Failed to save menu item.');
        }
    };

    return (
        <Row className="d-flex justify-content-center">
            <Col xs={12} sm={12} md={11} lg={11} className="shadow-lg p-5 m-3">
                <Formik
                    initialValues={{
                        itemName: menuItemDetails?.itemName || '',
                        type: menuItemDetails?.type || 'price',
                        price: menuItemDetails?.price ?? '',
                        discountPercent: menuItemDetails?.discountPercent ?? ''
                    }}
                    validationSchema={validate}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {(formik) => (
                        <Form>
                            <Row>
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Item Name</BootstrapForm.Label>
                                        <BootstrapForm.Control type="text" name="itemName" placeholder="Enter item name" {...formik.getFieldProps('itemName')} />
                                        {formik.errors.itemName && formik.touched.itemName && <div className="text-danger">{formik.errors.itemName}</div>}
                                    </BootstrapForm.Group>
                                </Col>
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Type</BootstrapForm.Label>
                                        <div className="d-flex gap-10 justify-content-start my-3 my-sm-0">
                                            <select
                                                name="type"
                                                value={formik.values.type}
                                                onChange={(e) => {
                                                    formik.handleChange(e);
                                                    // Reset values when type changes
                                                    if (e.target.value === 'price') {
                                                        formik.setFieldValue('discountPercent', '');
                                                    } else {
                                                        formik.setFieldValue('price', '');
                                                    }
                                                }}
                                                className="status-selector w-100"
                                                style={{ color: !formik.values.type ? '#9a9a9a' : '', padding: '7px 15px 8px 15px' }}
                                            >
                                                <option value="price">Price</option>
                                                <option value="discount">Discount</option>
                                            </select>
                                        </div>
                                        {formik.errors.type && formik.touched.type && <div className="text-danger">{formik.errors.type}</div>}
                                    </BootstrapForm.Group>
                                </Col>
                                {formik.values.type === 'price' ? (
                                    <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                        <BootstrapForm.Group>
                                            <BootstrapForm.Label>Price</BootstrapForm.Label>
                                            <BootstrapForm.Control type="number" name="price" placeholder="Enter price" step="0.01" {...formik.getFieldProps('price')} />
                                            {formik.errors.price && formik.touched.price && <div className="text-danger">{formik.errors.price}</div>}
                                        </BootstrapForm.Group>
                                    </Col>
                                ) : (
                                    <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                        <BootstrapForm.Group>
                                            <BootstrapForm.Label>Discount %</BootstrapForm.Label>
                                            <BootstrapForm.Control
                                                type="number"
                                                name="discountPercent"
                                                placeholder="Enter discount percent"
                                                step="0.01"
                                                {...formik.getFieldProps('discountPercent')}
                                            />
                                            {formik.errors.discountPercent && formik.touched.discountPercent && (
                                                <div className="text-danger">{formik.errors.discountPercent}</div>
                                            )}
                                        </BootstrapForm.Group>
                                    </Col>
                                )}
                            </Row>
                            <div className="text-end mt-4">
                                <Button
                                    type="button"
                                    className="theme-cancel-btn me-3"
                                    variant="secondary"
                                    onClick={() => navigate(state?.redirect_url || '/menu-items')}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" className="theme-btn" variant="primary">
                                    {isEdit ? 'Update' : 'Add'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </Col>
        </Row>
    );
}

export default AddEditMenuItem;
